import React, { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Trash2, Shield } from 'lucide-react';
import {
  fetchChannelMembers,
  addChannelMember,
  removeChannelMember,
  updateMemberRole,
  updateChannel,
  deleteChannel,
  isChannelAdmin
} from '../utils/chatHelpers';
import { supabase } from '../supabaseClient';

const ChannelSettingsModal = ({ channel, members, onClose, onUpdate }) => {
  const [channelMembers, setChannelMembers] = useState(members);
  const [allUsers, setAllUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [description, setDescription] = useState(channel.description || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'settings'
  const memberSubscriptionRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
    setupMemberSubscription();

    return () => {
      cleanupMemberSubscription();
    };
  }, []);

  // Sync local members state when prop changes
  useEffect(() => {
    setChannelMembers(members);
  }, [members]);

  const setupMemberSubscription = () => {
    console.log('[Modal] Setting up member subscription for channel:', channel.id);

    // Use unique channel name for modal to avoid conflicts with main component
    const memberSub = supabase
      .channel(`modal-members:${channel.id}:${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_chat',
          filter: `channel_id=eq.${channel.id}`
        },
        async (payload) => {
          if (payload.new.record_type !== 'member') return;

          console.log('[Modal] Member added via subscription:', payload);

          // Fetch user details
          const { data: user } = await supabase
            .from('users')
            .select('id, name, email, is_active')
            .eq('id', payload.new.user_id)
            .single();

          const member = {
            id: payload.new.id,
            channel_id: payload.new.channel_id,
            user_id: payload.new.user_id,
            role: payload.new.user_role,
            joined_at: payload.new.created_at,
            user: user
          };

          setChannelMembers(prev => {
            if (prev.some(m => m.id === member.id)) return prev;
            console.log('[Modal] Adding member. Old count:', prev.length, 'New count:', prev.length + 1);
            return [...prev, member];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'team_chat',
          filter: `channel_id=eq.${channel.id}`
        },
        (payload) => {
          if (!payload.old || payload.old.record_type !== 'member') return;

          console.log('[Modal] Member removed via subscription - ID:', payload.old.id, 'User ID:', payload.old.user_id);

          setChannelMembers(prev => {
            const updated = prev.filter(m => m.id !== payload.old.id);
            console.log('[Modal] Removing member. Old count:', prev.length, 'New count:', updated.length);
            return updated;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'team_chat',
          filter: `channel_id=eq.${channel.id}`
        },
        async (payload) => {
          if (payload.new.record_type !== 'member') return;

          console.log('[Modal] Member updated via subscription:', payload);

          const { data: user } = await supabase
            .from('users')
            .select('id, name, email')
            .eq('id', payload.new.user_id)
            .single();

          const member = {
            id: payload.new.id,
            channel_id: payload.new.channel_id,
            user_id: payload.new.user_id,
            role: payload.new.user_role,
            joined_at: payload.new.created_at,
            user: user
          };

          setChannelMembers(prev => prev.map(m => m.id === member.id ? member : m));
        }
      )
      .subscribe((status) => {
        console.log('[Modal] Member subscription status:', status);
      });

    memberSubscriptionRef.current = memberSub;
  };

  const cleanupMemberSubscription = () => {
    if (memberSubscriptionRef.current) {
      console.log('[Modal] Cleaning up member subscription');
      supabase.removeChannel(memberSubscriptionRef.current);
      memberSubscriptionRef.current = null;
    }
  };

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, email, is_active')
      .order('name');

    if (!error && data) {
      setAllUsers(data);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;

    setIsUpdating(true);
    const { error } = await addChannelMember(channel.id, selectedUser);

    if (!error) {
      setSelectedUser('');
      // Real-time subscription will update the member list automatically
    } else {
      alert(error.message || 'Failed to add member');
    }

    setIsUpdating(false);
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    setIsUpdating(true);
    const { error } = await removeChannelMember(channel.id, userId);

    if (!error) {
      // Immediately refetch members to ensure UI is in sync
      console.log('[Modal] Member removed, refetching member list');
      const { data } = await fetchChannelMembers(channel.id);
      if (data) {
        setChannelMembers(data);
      }
    } else {
      alert(error.message || 'Failed to remove member');
    }

    setIsUpdating(false);
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';

    setIsUpdating(true);
    const { error } = await updateMemberRole(channel.id, userId, newRole);

    if (!error) {
      setChannelMembers(prev => prev.map(m =>
        m.user_id === userId ? { ...m, role: newRole } : m
      ));
    } else {
      alert(error.message || 'Failed to update member role');
    }

    setIsUpdating(false);
  };

  const handleUpdateDescription = async () => {
    setIsUpdating(true);
    const { error } = await updateChannel(channel.id, { description });

    if (!error) {
      onUpdate();
    }

    setIsUpdating(false);
  };

  const handleDeleteChannel = async () => {
    if (!confirm(`Are you sure you want to delete #${channel.name}? This cannot be undone.`)) return;

    setIsUpdating(true);
    const { error } = await deleteChannel(channel.id);

    if (!error) {
      onClose();
      onUpdate();
    }

    setIsUpdating(false);
  };

  const isAdmin = currentUser && isChannelAdmin(channelMembers, currentUser.id);
  const isCreator = currentUser && channel.created_by === currentUser.id;

  // Filter out users already in the channel
  const availableUsers = allUsers.filter(
    user => !channelMembers.some(m => m.user_id === user.id)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-2xl w-full max-h-[80vh] overflow-hidden rounded-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold text-white">#{channel.name}</h2>
            <p className="text-sm text-gray-400">Channel Settings</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'members'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
              }`}
          >
            Members ({channelMembers.length})
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'settings'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              Settings
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'members' && (
            <div className="space-y-4">
              {/* Add Member */}
              {isAdmin && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <h3 className="text-sm font-medium text-white mb-3">Add Member</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors [&>option]:bg-slate-800 [&>option]:text-white"
                      disabled={isUpdating}
                    >
                      <option value="" className="bg-slate-800 text-white">Select a user</option>
                      {availableUsers.filter(user => user.is_active !== false).map(user => (
                        <option key={user.id} value={user.id} className="bg-slate-800 text-white">
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddMember}
                      disabled={!selectedUser || isUpdating}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Members List */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Channel Members</h3>
                {channelMembers.map((member) => {
                  const isInactive = member.user?.is_active === false;
                  const displayName = isInactive && member.user?.inactive_user_name 
                    ? member.user.inactive_user_name 
                    : (member.user?.name || 'Unknown User');
                  return (
                  <div
                    key={member.id}
                    className={`flex items-center justify-between p-3 bg-white/5 rounded-lg ${isInactive ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${isInactive ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-purple-500'} flex items-center justify-center text-white text-sm font-semibold`}>
                        {member.user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isInactive ? 'text-gray-400' : 'text-white'}`}>
                          {displayName}
                        </p>
                        <p className="text-xs text-gray-400">{member.user?.email}</p>
                      </div>
                      {member.role === 'admin' && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                          Admin
                        </span>
                      )}
                      {channel.created_by === member.user_id && (
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded">
                          Creator
                        </span>
                      )}
                    </div>

                    {isAdmin && member.user_id !== currentUser?.id && (
                      <div className="flex items-center gap-2">
                        {/* Only show admin toggle if not the creator and current user is creator */}
                        {channel.created_by !== member.user_id && isCreator && (
                          <button
                            onClick={() => handleToggleAdmin(member.user_id, member.role)}
                            disabled={isUpdating}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title={member.role === 'admin' ? 'Remove admin' : 'Make admin'}
                          >
                            <Shield className={`w-4 h-4 ${member.role === 'admin' ? 'text-blue-400' : 'text-gray-400'}`} />
                          </button>
                        )}
                        {/* Only show remove button if: not the creator AND (current user is creator OR member is not admin) */}
                        {channel.created_by !== member.user_id && (isCreator || member.role !== 'admin') && (
                          <button
                            onClick={() => handleRemoveMember(member.user_id)}
                            disabled={isUpdating}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Remove from channel"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            </div>
          )}

          {activeTab === 'settings' && isAdmin && (
            <div className="space-y-6">
              {/* Channel Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Channel Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this channel..."
                  rows={4}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  disabled={isUpdating}
                />
                <button
                  onClick={handleUpdateDescription}
                  disabled={isUpdating}
                  className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                >
                  {isUpdating ? 'Updating...' : 'Update Description'}
                </button>
              </div>

              {/* Danger Zone */}
              {isCreator && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <h3 className="text-sm font-medium text-red-400 mb-2">Danger Zone</h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Deleting this channel will remove all messages and cannot be undone.
                  </p>
                  <button
                    onClick={handleDeleteChannel}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    Delete Channel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsModal;
